import { useEffect, useMemo, useState } from 'react';
import { CustomModal } from '../CustomModal';
import {
    Btn,
    IconImage,
    IconPdf,
    IconVideo2,
    Pagination,
    SearchToolbar,
    TEIQspinner,
} from '../ui';
import type { SearchSuggestOption } from '../ui';
import type { BriefingStyle, BriefingTopic } from './dailyBriefingData';
import { formatLastUsedLabel, isPdfBriefingTopic } from './dailyBriefingData';

const CHANGE_TOPIC_PAGE_SIZE = 20;

type StyleFilter = 'All' | BriefingStyle;

const STYLE_FILTER_OPTIONS: { value: StyleFilter; label: string }[] = [
    { value: 'All', label: 'All' },
    { value: 'Video', label: 'Video' },
    { value: 'PDF', label: 'PDF' },
    { value: 'Poster', label: 'Poster' },
];

function TopicTypeIcon({ topic }: { topic: BriefingTopic }) {
    if (topic.type === 'video') {
        return <IconVideo2 iconClasses="db-change-type-svg" />;
    }
    if (isPdfBriefingTopic(topic)) {
        return <IconPdf iconClasses="db-change-type-svg" />;
    }
    return <IconImage iconClasses="db-change-type-svg" />;
}

function topicTypeClass(topic: BriefingTopic): string {
    if (topic.type === 'video') return 'db-change-type-icon--video';
    if (isPdfBriefingTopic(topic)) return 'db-change-type-icon--pdf';
    return 'db-change-type-icon--poster';
}

function topicMetaLine(topic: BriefingTopic): string {
    const parts = [topic.displayId, topic.style];
    if (!isPdfBriefingTopic(topic)) {
        parts.push('Duration —');
    }
    return parts.join(' · ');
}

interface ChangeTopicModalProps {
    visible: boolean;
    topics: BriefingTopic[];
    currentTopic: BriefingTopic | null;
    currentTopicId: string | null;
    confirmedCount: number;
    crewCount: number;
    saving: boolean;
    onDismiss: () => void;
    onSelect: (topic: BriefingTopic) => Promise<void>;
}

export function ChangeTopicModal({
    visible,
    topics,
    currentTopic,
    currentTopicId,
    confirmedCount,
    crewCount,
    saving,
    onDismiss,
    onSelect,
}: ChangeTopicModalProps) {
    const [selectedId, setSelectedId] = useState<string | null>(currentTopicId);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [styleFilter, setStyleFilter] = useState<StyleFilter>('All');

    useEffect(() => {
        if (visible) {
            setSelectedId(currentTopicId);
            setCurrentPage(1);
            setSearchValue('');
            setStyleFilter('All');
        }
    }, [visible, currentTopicId]);

    const searchOptions = useMemo<SearchSuggestOption[]>(
        () =>
            topics.map((topic) => ({
                value: topic.id,
                label: `${topic.title} (${topic.displayId})`,
            })),
        [topics],
    );

    const filteredTopics = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        return topics.filter((topic) => {
            if (styleFilter !== 'All' && topic.style !== styleFilter) {
                return false;
            }
            if (!query) return true;
            if (topic.id.toLowerCase() === query) return true;
            return (
                topic.title.toLowerCase().includes(query) ||
                topic.displayId.toLowerCase().includes(query) ||
                `${topic.title} (${topic.displayId})`.toLowerCase().includes(query)
            );
        });
    }, [searchValue, styleFilter, topics]);

    const totalPages = Math.max(1, Math.ceil(filteredTopics.length / CHANGE_TOPIC_PAGE_SIZE));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, styleFilter]);

    const pageTopics = useMemo(() => {
        const pageIndex = Math.min(currentPage, totalPages);
        const start = (pageIndex - 1) * CHANGE_TOPIC_PAGE_SIZE;
        return filteredTopics.slice(start, start + CHANGE_TOPIC_PAGE_SIZE);
    }, [currentPage, filteredTopics, totalPages]);

    const handleConfirm = async () => {
        const topic = topics.find((t) => t.id === selectedId);
        if (!topic) return;
        await onSelect(topic);
    };

    const rangeStart =
        filteredTopics.length === 0
            ? 0
            : (Math.min(currentPage, totalPages) - 1) * CHANGE_TOPIC_PAGE_SIZE + 1;
    const rangeEnd = Math.min(
        Math.min(currentPage, totalPages) * CHANGE_TOPIC_PAGE_SIZE,
        filteredTopics.length,
    );

    return (
        <CustomModal
            visible={visible}
            onDismiss={onDismiss}
            header="Change Today's Topic"
            size="large"
            bodyClassName="db-change-topic-modal-body"
            describedById="db-change-topic-note"
            footer={
                <div className="db-modal-footer-actions">
                    <Btn
                        variant="secondary"
                        buttonText="Cancel"
                        buttonOnClick={onDismiss}
                        buttonDisabled={saving}
                    />
                    <Btn
                        variant="warning"
                        buttonText={saving ? 'Saving…' : "Set as Today's Topic"}
                        buttonOnClick={handleConfirm}
                        buttonDisabled={saving || !selectedId || selectedId === currentTopicId}
                    />
                </div>
            }
        >
            <div className="db-change-topic">
                {currentTopic && (
                    <section className="db-change-section" aria-label="Currently today's topic">
                        <h3 className="db-change-section-label">Currently today&apos;s topic</h3>
                        <div className="db-change-current">
                            <span
                                className={`db-change-type-icon ${topicTypeClass(currentTopic)}`}
                                aria-hidden="true"
                            >
                                <TopicTypeIcon topic={currentTopic} />
                            </span>
                            <div className="db-change-current-copy">
                                <p className="db-change-current-title">{currentTopic.title}</p>
                                <p className="db-change-current-meta">
                                    {topicMetaLine(currentTopic)}
                                    {crewCount > 0
                                        ? ` · ${confirmedCount} of ${crewCount} crew confirmed today`
                                        : ''}
                                </p>
                            </div>
                        </div>
                    </section>
                )}

                <SearchToolbar
                    searchInputId="db-change-topic-search"
                    searchAriaLabel="Search topic library"
                    searchPlaceholder="Search library by name or ID..."
                    searchOptions={searchOptions}
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    allowNewSearch={false}
                    bordered={false}
                    filterOptions={STYLE_FILTER_OPTIONS}
                    filterValue={styleFilter}
                    onFilterChange={(value) => setStyleFilter(value as StyleFilter)}
                    filterAriaLabel="Filter by style"
                    disabled={saving}
                />

                <section className="db-change-section" aria-label="Topic library">
                    <h3 className="db-change-section-label">
                        Topic library · {filteredTopics.length} topic
                        {filteredTopics.length === 1 ? '' : 's'}
                    </h3>

                    {filteredTopics.length === 0 ? (
                        <p className="db-empty-state">
                            {topics.length === 0
                                ? 'Add topics to your library before assigning today’s briefing.'
                                : 'No topics match your search or filter.'}
                        </p>
                    ) : (
                        <>
                            <ul
                                className="db-change-list"
                                role="radiogroup"
                                aria-label="Select a topic"
                                onKeyDown={(event) => {
                                    if (saving || pageTopics.length === 0) return;
                                    const currentIndex = pageTopics.findIndex(
                                        (topic) => topic.id === selectedId,
                                    );
                                    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                                        event.preventDefault();
                                        const next =
                                            currentIndex < 0
                                                ? 0
                                                : (currentIndex + 1) % pageTopics.length;
                                        setSelectedId(pageTopics[next].id);
                                        (
                                            event.currentTarget.querySelectorAll<HTMLButtonElement>(
                                                '[role="radio"]',
                                            )[next]
                                        )?.focus();
                                    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                                        event.preventDefault();
                                        const next =
                                            currentIndex < 0
                                                ? pageTopics.length - 1
                                                : (currentIndex - 1 + pageTopics.length) %
                                                  pageTopics.length;
                                        setSelectedId(pageTopics[next].id);
                                        (
                                            event.currentTarget.querySelectorAll<HTMLButtonElement>(
                                                '[role="radio"]',
                                            )[next]
                                        )?.focus();
                                    }
                                }}
                            >
                                {pageTopics.map((topic) => {
                                    const selected = selectedId === topic.id;
                                    return (
                                        <li key={topic.id}>
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                className={`db-change-row${selected ? ' is-selected' : ''}`}
                                                onClick={() => setSelectedId(topic.id)}
                                                disabled={saving}
                                            >
                                                <span
                                                    className={`db-change-radio${selected ? ' is-checked' : ''}`}
                                                    aria-hidden="true"
                                                />
                                                <span
                                                    className={`db-change-type-icon ${topicTypeClass(topic)}`}
                                                    aria-hidden="true"
                                                >
                                                    <TopicTypeIcon topic={topic} />
                                                </span>
                                                <span className="db-change-row-copy">
                                                    <span className="db-change-row-title">{topic.title}</span>
                                                    <span className="db-change-row-meta">
                                                        {topicMetaLine(topic)}
                                                    </span>
                                                </span>
                                                <span className="db-change-row-used">
                                                    Last used {formatLastUsedLabel(topic.lastUsedDate)}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="db-change-topic-pagination">
                                <span className="db-library-range" aria-live="polite">
                                    Showing {rangeStart}–{rangeEnd} of {filteredTopics.length}
                                </span>
                                {totalPages > 1 && (
                                    <Pagination
                                        className="db-topic-pagination"
                                        currentPage={Math.min(currentPage, totalPages)}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                        ariaLabel="Change topic pagination"
                                    />
                                )}
                            </div>
                        </>
                    )}
                </section>

                <div className="db-change-note" id="db-change-topic-note" role="note">
                    <span className="db-change-note-icon" aria-hidden="true">
                        i
                    </span>
                    <p>
                        The new topic becomes today&apos;s briefing immediately. Crew who haven&apos;t
                        confirmed the previous topic will see the new one on their next login.
                        Confirmations already collected today are preserved in records.
                    </p>
                </div>
            </div>

            {saving && (
                <div className="db-modal-loading">
                    <TEIQspinner />
                </div>
            )}
        </CustomModal>
    );
}
